/**
 * El programa de socios (CLAUDE.md §30): las reglas, en un solo sitio.
 *
 * Copiadas del programa de Zygore, que es el que ya funciona en la casa: todo
 * cliente es socio sin alta; gana el 20 % del neto sin impuestos del **primer**
 * pago de cada cliente que trae; una agencia socia, aprobada por una persona, el
 * 20 % de **todos** los pagos mientras el cliente siga; cada comisión madura a los
 * 45 días (el plazo en que un pago se puede devolver); y el pago se pide desde
 * $50 y lo envía una persona por PayPal. El referido tiene el 20 % de descuento
 * en su primer pago.
 *
 * Los importes van en **céntimos enteros** (`@mc/comercio`, §23): el dinero no se
 * cuenta en flotantes.
 */
export const COMISION_PORCENTAJE = 20
export const DIAS_DE_MADURACION = 45
export const MINIMO_DE_PAGO_CENTIMOS = 50_00
/** Por encima de esto, el pago no se aprueba desde el panel: lo mira una persona con más calma. */
export const MAXIMO_DE_PAGO_CENTIMOS = 2000_00
export const DIAS_DE_COOKIE = 180
export const DESCUENTO_DEL_REFERIDO_PORCENTAJE = 20
/** Solo se atribuye una cuenta **nueva**: creada hace menos de esto y sin ningún cobro. */
export const DIAS_PARA_ATRIBUIR = 30
export const MONEDA = 'USD'
export const PARAMETRO_DE_SOCIO = 'ref'
export const COOKIE_DE_SOCIO = 'wixy_socio'
/** El impuesto (18 %): lo que se descuenta del importe cobrado para sacar la base de la comisión. */
export const IMPUESTO_PORCENTAJE = 18

export const ESTADOS_DE_SOCIO = ['activo', 'pausado'] as const
export const ESTADOS_DE_COMISION = ['pendiente', 'aprobada', 'pagada', 'anulada'] as const
/** `enviado`: PayPal lo tiene y lo está entregando; `fallido`: PayPal no pudo entregarlo (§31). */
export const ESTADOS_DE_PAGO = ['solicitado', 'aprobado', 'enviado', 'pagado', 'rechazado', 'fallido'] as const
export const ESTADOS_DE_AGENCIA = ['solicitada', 'aprobada', 'rechazada'] as const
export const METODOS_DE_PAGO = ['paypal', 'manual'] as const

export type EstadoDeSocio = (typeof ESTADOS_DE_SOCIO)[number]
export type EstadoDeComision = (typeof ESTADOS_DE_COMISION)[number]
export type EstadoDePago = (typeof ESTADOS_DE_PAGO)[number]
export type EstadoDeAgencia = (typeof ESTADOS_DE_AGENCIA)[number]
export type MetodoDePago = (typeof METODOS_DE_PAGO)[number]

/** La descripción de una agencia que pide entrar: lo bastante larga para que una persona sepa quién es. */
export const DESCRIPCION_DE_AGENCIA = { minimo: 200, maximo: 1000 } as const

const entero = (n: number): number => (Number.isFinite(n) ? Math.round(n) : 0)

/** La base de una comisión: lo cobrado sin el impuesto, en céntimos. Si el cobro no llevaba impuesto, es el propio importe. */
export const baseSinImpuesto = (importeCentimos: number, incluyeImpuesto: boolean, impuestoPorcentaje = IMPUESTO_PORCENTAJE): number => {
  const importe = entero(importeCentimos)
  if (importe <= 0) return 0
  if (!incluyeImpuesto || impuestoPorcentaje <= 0) return importe
  return Math.round((importe * 100) / (100 + impuestoPorcentaje))
}

/** La comisión sobre una base, en céntimos. Cero si la base no es un importe. */
export const comisionDe = (baseCentimos: number, porcentaje = COMISION_PORCENTAJE): number => {
  const base = entero(baseCentimos)
  if (base <= 0 || porcentaje <= 0) return 0
  return Math.round((base * porcentaje) / 100)
}

export const fechaDeMaduracion = (pagadoEn: Date, dias = DIAS_DE_MADURACION): Date => new Date(pagadoEn.getTime() + dias * 86_400_000)

export type ComisionParaSaldo = { estado: EstadoDeComision; importe: number; pago?: null | number | { id: number } }

export type SaldoDeSocio = {
  /** Aprobadas y sin pago pedido: lo que se puede pedir ahora. */
  disponible: number
  /** Todavía madurando. */
  pendiente: number
  /** Aprobadas y ya dentro de un pago pedido. */
  reservado: number
  /** Lo que ya se envió. */
  pagado: number
  /** Cuánto falta para llegar al mínimo; cero si ya se llega. */
  faltaParaPedir: number
}

export const saldoDeSocio = (comisiones: ComisionParaSaldo[]): SaldoDeSocio => {
  const saldo = { disponible: 0, pendiente: 0, reservado: 0, pagado: 0, faltaParaPedir: 0 }
  for (const c of comisiones) {
    const importe = entero(c.importe)
    if (c.estado === 'pendiente') saldo.pendiente += importe
    else if (c.estado === 'pagada') saldo.pagado += importe
    else if (c.estado === 'aprobada') {
      if (c.pago) saldo.reservado += importe
      else saldo.disponible += importe
    }
  }
  saldo.faltaParaPedir = Math.max(0, MINIMO_DE_PAGO_CENTIMOS - saldo.disponible)
  return saldo
}

/** `brucevladimir@gmail.com` → `bru**********@gmail.com`: el socio sabe a quién trajo sin llevarse su correo. */
export const enmascararCorreo = (correo: string): string => {
  const [usuario = '', dominio] = correo.split('@')
  if (!dominio) return correo.length <= 3 ? '***' : `${correo.slice(0, 3)}${'*'.repeat(Math.max(3, correo.length - 3))}`
  const visible = usuario.slice(0, Math.min(3, Math.max(1, usuario.length - 1)))
  return `${visible}${'*'.repeat(Math.max(3, usuario.length - visible.length))}@${dominio}`
}

/** El enlace que comparte el socio. `base` es la dirección de la web comercial (sin barra final). */
export const enlaceDeSocio = (base: string, codigo: string, destino = ''): string => {
  const raiz = base.replace(/\/+$/, '')
  const ruta = destino ? `/${destino.replace(/^\/+/, '')}` : ''
  return `${raiz}${ruta}?${PARAMETRO_DE_SOCIO}=${encodeURIComponent(codigo)}`
}

/** Un correo de PayPal es un correo. */
export const RE_CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/**
 * Clics que no cuentan: robots, rastreadores y las vistas previas que WhatsApp o
 * Telegram piden al pegar un enlace. Un `User-Agent` corto tampoco es un navegador.
 */
export const pareceRobot = (userAgent: null | string | undefined): boolean => {
  const ua = (userAgent ?? '').trim()
  if (ua.length < 12) return true
  return /bot|crawl|spider|slurp|whatsapp|telegram|facebookexternalhit|twitterbot|linkedinbot|discordbot|curl|wget|python|httpclient|headless|axios|node-fetch|go-http|java\/|okhttp|preview|embedly|quora|pinterest/i.test(ua)
}

export const motivoDeDescripcionDeAgenciaInvalida = (descripcion: string): null | string => {
  const largo = descripcion.trim().length
  if (largo < DESCRIPCION_DE_AGENCIA.minimo) return `Cuéntanos un poco más: al menos ${DESCRIPCION_DE_AGENCIA.minimo} caracteres (llevas ${largo}).`
  if (largo > DESCRIPCION_DE_AGENCIA.maximo) return `Resume un poco: como mucho ${DESCRIPCION_DE_AGENCIA.maximo} caracteres (llevas ${largo}).`
  return null
}

/** El correo de una agencia tiene que ser del dominio de su web: es lo que dice que quien pide es quien dice ser. */
export const motivoDeCorreoDeAgenciaInvalido = (correo: string, web: string): null | string => {
  if (!RE_CORREO.test(correo)) return 'Escribe un correo válido.'
  let dominio: string
  try {
    dominio = new URL(web.startsWith('http') ? web : `https://${web}`).hostname.replace(/^www\./, '').toLowerCase()
  } catch {
    return 'Escribe la dirección de tu web, por ejemplo https://tuagencia.pe.'
  }
  if (!dominio.includes('.')) return 'Escribe la dirección de tu web, por ejemplo https://tuagencia.pe.'
  const delCorreo = correo.split('@')[1]!.toLowerCase()
  if (delCorreo !== dominio && !delCorreo.endsWith(`.${dominio}`)) return `El correo tiene que ser del dominio de tu web (@${dominio}): así sabemos que la agencia es tuya.`
  return null
}
